import React, { FunctionComponent, useState, useEffect } from "react";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Sector } from "recharts";
import styles from "./PendingTickets.module.css";
import { useLazyQuery } from "@apollo/client";
import { GET_PENDING_TICKETS } from "../api/DashboardQuery";
import { formatNumberWithCommas, predefinedColors, roundToFixed, specificColors } from "../../../helper";
import { CircularProgress } from "@mui/material";

interface pieDataType {
  id: string,
  name: string,
  value: number,
  color?: string
}
interface ActiveTicketsDataType{
  leagueName: string,
  activeTickets: number
}
const PendingTickets: FunctionComponent = () => {
  const [chartDimensions, setChartDimensions] = useState({
    width: 0,
    height: 0,
  });
  type DataType = {
    name: string;
    value: number;
  };
  const extractLeaguesNames = ['WWE','Theatres','Concert'];

  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const [pieChartData,setPieChartData] = useState<pieDataType[] | []>([]);
  const [piechart,setPieChart] = useState<pieDataType[] | []>([]);
  const [pendingTicketsTotalCount,setPendingTicketsTotalCount] = useState<number>();
  const [potentialpayout, setPotentialPayout] = useState<number>(0);
  const [getPendingTickets,{loading}] = useLazyQuery(GET_PENDING_TICKETS,{
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
       const transformedData = data?.getPendingTicketsDashboard?.getPendingActiveTicketsByLeague
                               .filter((item: ActiveTicketsDataType) => !extractLeaguesNames.includes(item?.leagueName))
                               .map((result: ActiveTicketsDataType,index: number) => {
                                  return {
                                    id: `${result?.leagueName}-${index}`,
                                    name: result?.leagueName,
                                    value: result?.activeTickets,
                                    color: specificColors[result?.leagueName]
                                  }
                               })
          if(data?.getPendingTicketsDashboard?.getPendingActiveTicketsCountAndPayout[0]?.totalCount){
            setPendingTicketsTotalCount(data?.getPendingTicketsDashboard?.getPendingActiveTicketsCountAndPayout[0]?.totalCount);
            const payout = roundToFixed(data?.getPendingTicketsDashboard?.getPendingActiveTicketsCountAndPayout[0]?.potentialPayout, 2);
            setPotentialPayout(Number(payout));
            setPieChartData(transformedData);
            setPieChart(transformedData);
          }else {
            setPendingTicketsTotalCount(0);
            setPieChartData(transformedData);
            setPieChart([{
              id: `nodata`,
              name: 'nodata',
              value: 1,
              color: '#F3F4F6'
            }]);
          }
    }
  })
  const handleMouseEnter = (_data: DataType[], index: number) => {
    setActiveIndex(index);
  };

  const handleMouseLeave = () => {
    setActiveIndex(-1);
  };

  const activeShape = (props: any) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
    return (
      <g>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius - 3}
          outerRadius={outerRadius + 1} 
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
      </g>
    );
  };


  const CustomTooltip = ({ active, payload }: any) => {
    if (active  && payload && payload.length) {
      const dataItem = payload[0]?.payload;
      return !(dataItem.name === 'nodata') ?
      (
        <div className={styles.toolTipContainer}>
          <div className={styles.toolTipName}>{dataItem.name}</div>
          <div className={styles.toolTipDivider} />
          <div
            style={{ color: dataItem.payload?.color }}
            className={styles.toolTipValue}
          >
            {dataItem.value}
          </div>
        </div>
      ) :
      (<div></div>);
    }

    return null;
  };

  const handleResize = () => {
      setChartDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
  };
  useEffect(() => {
    getPendingTickets();
    handleResize(); // Set initial dimensions
    window.addEventListener("resize", handleResize); // Update dimensions on resize
    return () => window.removeEventListener("resize", handleResize); // Remove listener on unmount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const outerRadiusPercentage = 13; // Adjust this percentage for outer radius
  const innerRadiusPercentage = 9.5; // Adjust this percentage for inner radius

  const calculateRadius = (dimension: number, percentage: number) => {
    return (dimension / 2) * (percentage / 100);
  };

  const outerRadius = calculateRadius(
    chartDimensions.width,
    outerRadiusPercentage
  );
  const innerRadius = calculateRadius(
    chartDimensions.width,
    innerRadiusPercentage
  );
  return (
    <div className={styles.pendingTickets}>
      <div className={styles.topSales3}>
        <div className={styles.pendingTicketsLabel}>Pending Tickets</div>
        <div><span className={styles.potentialPayout} style={{fontSize: '14px'}}>Potential Payout: </span><span className={styles.payoutAmt}>${formatNumberWithCommas(potentialpayout)}</span></div>
      </div>
      {
        !(loading)
      ?
          (pieChartData.length > 0)
          ?   <div className={styles.pendingTicketsContainer}>
          <div className={styles.piechartcontainer}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <text
                  className={styles.piechartCenterName}
                  x={"50%"}
                  y={"45%"}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill="#B9BDC6"
                >
                  Total Pending
                </text>
                <text
                  className={styles.piechartCenterValue}
                  x={"50%"}
                  y={"57%"}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill="#151A34"
                >
                  {pendingTicketsTotalCount}
                </text>
                <Pie
                  activeIndex={activeIndex}
                  activeShape={activeShape}
                  data={piechart}
                  innerRadius={`${innerRadius}`}
                  outerRadius={`${outerRadius}`}
                  dataKey="value"
                  cx="50%"
                  cy="50%"
                  paddingAngle={0}
                  stroke="none"
                  onMouseEnter={(_data, index) => handleMouseEnter(_data, index)}
                  onMouseLeave={handleMouseLeave}
                >
                  {piechart.length > 0 && piechart?.map((entry: pieDataType, index: number) => {
                    return <Cell
                    key={entry.id}
                    fill={specificColors.hasOwnProperty(entry.name)
                      ? specificColors[entry.name]
                      : predefinedColors[index % predefinedColors.length]}
                    className={activeIndex === index ? styles.animateCell : ""}
                  />
                  })}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className={styles.piechartDetails}>
            <table className={styles.piechartDetailsTable}>
            <tbody>
              {pieChartData.length > 0 && pieChartData?.map((entry, index) => (
                  <tr key={`${entry.value}-${index}`} className={styles.rowStyle}>
                    <td className={styles.legendColorBox}>
                      <div
                        style={{
                          width: 8,
                          height: 8,
                          backgroundColor:specificColors.hasOwnProperty(entry.name)
                          ? specificColors[entry.name]
                          : predefinedColors[index % predefinedColors.length],
                        }}
                      />
                    </td>
                    <td className={styles.legendName}>
                      <span className={styles.piechartname}>{entry.name} </span>
                    </td>
                    <td className={styles.legendValue}>
                      <span className={styles.piechartValue}>{entry.value}</span>
                    </td>
                  </tr>
              ))}
              </tbody>
            </table>
          </div>
              </div>
          :   <div className={styles.nodatafound}>No data found</div>
      :  <div className={styles.nodatafound}><CircularProgress color="primary" size={30} /></div>
    }
    </div>
  );
};

export default PendingTickets;
